import { useEffect } from 'react';

const SITE = 'https://healthwrx.in';

/**
 * useSeo — manages document.title, meta description, JSON-LD schema, and the
 * canonical URL on mount. Cleans up on unmount so SPA navigation works correctly.
 *
 * @param {Object} opts
 * @param {string} [opts.title]
 * @param {string} [opts.description]
 * @param {string} [opts.canonical]   Absolute canonical URL. Defaults to SITE + window.location.pathname.
 * @param {Array}  [opts.jsonLd=[]]   JSON-LD blocks.
 */
export function useSeo({ title, description, canonical, jsonLd = [] }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensureMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute('content') || '';
      tag.setAttribute('content', content || '');
      return prev;
    };

    const prevDesc = description ? ensureMeta('description', description) : '';

    // Canonical link — replace existing if any, otherwise create.
    const canonicalHref =
      canonical ||
      (typeof window !== 'undefined'
        ? `${SITE}${window.location.pathname}`
        : SITE);
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    let prevCanonical = '';
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
      createdCanonical = true;
    } else {
      prevCanonical = canonicalTag.getAttribute('href') || '';
    }
    canonicalTag.setAttribute('href', canonicalHref);

    const scripts = [];
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    blocks.forEach((block) => {
      if (!block) return;
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo', 'symptom');
      s.text = JSON.stringify(block);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      document.title = prevTitle;
      if (description) ensureMeta('description', prevDesc);
      if (createdCanonical) {
        canonicalTag.remove();
      } else if (prevCanonical) {
        canonicalTag.setAttribute('href', prevCanonical);
      }
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, jsonLd]);
}

export default useSeo;
